// Display and reply helpers shared by the mail views.

const SENDING = {
  scheduled: ['Scheduled', 'accent'],
  queued: ['Sending', 'neutral'],
  held: ['Waiting', 'warning'],
  failed: ['Not sent', 'danger'],
  bounced: ['Bounced', 'warning'],
}

/** Adds the display fields every list, thread and reader row uses: `who`, `toLabel`, `unread`, `sendLabel`, `sendTone`. */
export function decorate(email) {
  const to = (email.to || []).map((item) => item.address)
  email.toLabel = to.length ? to.join(', ') : email.address
  email.who = email.direction === 'out' ? `To: ${email.toLabel}` : email.from_name || email.from_addr || 'Unknown sender'
  email.unread = !email.seen
  const sending = SENDING[email.send_status]
  email.sendLabel = sending ? sending[0] : ''
  email.sendTone = sending ? sending[1] : ''
  return email
}

/** SPF, DMARC, DKIM and spam results as `[{ label, tone }]` badges. */
export function authRows(auth) {
  if (!auth) return []
  const tone = (result) => (result === 'pass' ? 'success' : result === 'none' ? 'neutral' : 'warning')
  const rows = []
  if (auth.spf) rows.push({ label: `SPF ${auth.spf}`, tone: tone(auth.spf) })
  if (auth.dmarc) rows.push({ label: `DMARC ${auth.dmarc}`, tone: tone(auth.dmarc) })
  for (const result of auth.dkim || []) rows.push({ label: `DKIM ${result}`, tone: tone(result) })
  if (auth.spam !== undefined && auth.spam !== null) rows.push({ label: `Spam ${auth.spam}`, tone: 'neutral' })
  return rows
}

/** Downloads base64 data as a file. */
export function saveBase64(data, filename, type) {
  saveBlob(new Blob([Uint8Array.from(atob(data), (char) => char.charCodeAt(0))], { type }), filename)
}

/** Downloads text (a calendar file) as a file. */
export function saveText(text, filename, type) {
  saveBlob(new Blob([text], { type }), filename)
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** `Re: ` or `Fwd: ` in front of a subject that does not have it yet. */
export function prefixSubject(prefix, subject) {
  const text = (subject || '').trim()
  if (!text) return ''
  return text.toLowerCase().startsWith(prefix.toLowerCase()) ? text : `${prefix} ${text}`
}

/** `billing+eu@Acme.io` -> `billing@acme.io`. */
export function bareAddress(address) {
  const [local, host] = (address || '').toLowerCase().split('@')
  return host ? `${local.split('+')[0]}@${host}` : local.split('+')[0]
}

/** How a list row names one address: `{ name, email }` with the address beside the name, or the address (else the name) alone as `name`. */
export function person(name, address) {
  if (!address) return { name: name || '', email: '' }
  return name && name.toLowerCase() !== address.toLowerCase() ? { name, email: address } : { name: address, email: '' }
}

/** `person` entries as a tooltip: `Ana Novak <ana@acme.com>, luka@x.hr`. */
export function peopleTitle(people) {
  return people.map((item) => (item.email ? `${item.name} <${item.email}>` : item.name)).join(', ')
}

/**
 * Who a conversation row names from its `participants` (latest sender first): every other sender
 * as `person`, then `me` when one of `own` (bare addresses) wrote too; `Me` alone when nobody else did.
 */
export function threadPeople(participants, own) {
  const people = []
  let me = false
  for (const item of participants || []) {
    if (own.has(bareAddress(item.address))) me = true
    else people.push(person(item.name, item.address))
  }
  if (me || !people.length) people.push({ name: people.length ? 'me' : 'Me', email: '' })
  return people
}

/** A mailbox's name: the first of its addresses (`aliases`, in order). */
export function mailboxName(mailbox) {
  return mailbox?.aliases?.[0] || ''
}

/** Every address a mailbox receives at: each of its local parts (name first) on each domain. */
export function mailboxAddresses(mailbox, domains) {
  return (mailbox.aliases || []).flatMap((local) => domains.map((host) => `${local}@${host}`))
}

/**
 * To and Cc of a reply. A reply goes to Reply-To, else the sender (our own sent mail goes back to its
 * recipients); reply all adds every other recipient, minus our own addresses.
 */
export function replyRecipients(email, own = [], all = false) {
  const mine = new Set(own.map(bareAddress))
  const list = (value) => (value || []).map((item) => item.address).filter(Boolean)
  const primary = email.direction === 'out' ? list(email.to) : [email.reply_to || email.from_addr].filter(Boolean)
  const seen = new Set(primary.map(bareAddress))
  const cc = []
  if (all) {
    for (const address of [...list(email.to), ...list(email.cc)]) {
      const bare = bareAddress(address)
      if (mine.has(bare) || seen.has(bare)) continue
      seen.add(bare)
      cc.push(address)
    }
  }
  return { to: primary.join(', '), cc: cc.join(', ') }
}

/** The quoted original under a reply: its markdown body as a markdown blockquote. */
export function quoteBody(email, when) {
  const body = (email.md_body || '').trimEnd()
  const quote = body ? body.split('\n').map((line) => (line ? `> ${line}` : '>')).join('\n') : ''
  return `\n\nOn ${when}, ${email.from_name || email.from_addr || 'unknown sender'} wrote:\n\n${quote}`
}

/** The original below a forward. */
export function forwardBody(email, when) {
  const to = (email.to || []).map((item) => item.address).join(', ')
  const head = [
    '---------- Forwarded message ----------',
    `From: ${email.from_name ? `${email.from_name} <${email.from_addr}>` : email.from_addr || 'unknown'}`,
    `Date: ${when}`,
    `Subject: ${email.subject || ''}`,
    `To: ${to}`,
  ]
  return `\n\n${head.join('\n')}\n\n${email.md_body || ''}`
}

/** What a template may hold; compose fills them in when it inserts one. */
export const TEMPLATE_PLACEHOLDERS = ['{first_name}', '{name}', '{email}', '{mailbox}']

/** The first address of a recipient field: `"Novak, Ana" <Ana@x.com>, ivo@x.com` -> `{ name: 'Novak, Ana', email: 'Ana@x.com' }`. */
export function firstRecipient(field) {
  const text = field || ''
  let quoted = false
  let end = 0
  for (; end < text.length; end++) {
    const char = text[end]
    if (quoted && char === '\\') end++
    else if (char === '"') quoted = !quoted
    else if (!quoted && (char === ',' || char === ';')) break
  }
  const part = text.slice(0, end).trim()
  const angle = part.match(/^([^]*)<([^<>]*)>$/)
  const email = (angle ? angle[2] : part).trim()
  let name = angle ? angle[1].trim() : ''
  if (name.length > 1 && name.startsWith('"') && name.endsWith('"')) name = name.slice(1, -1).replace(/\\(.)/g, '$1')
  name = name.trim()
  return { name: name.includes('@') ? '' : name, email: email.includes('@') ? email : '' }
}

/**
 * A template's text with `{first_name}`, `{name}` and `{email}` of the first To recipient (the name
 * written in the field, else `contact`, my contact's name for that address) and `{mailbox}`, the From
 * mailbox's name. A placeholder with no value stays as written; any other `{word}` is left alone.
 */
export function fillTemplate(text, { to, contact, mailbox } = {}) {
  const recipient = firstRecipient(to)
  const name = recipient.name || (contact || '').trim()
  // `Novak, Ana` puts the family name first.
  const comma = name.indexOf(',')
  const first = (comma < 0 ? name : name.slice(comma + 1)).trim().split(/\s+/)[0]
  const values = { first_name: first, name, email: recipient.email, mailbox: (mailbox || '').trim() }
  return (text || '').replace(/\{(first_name|name|email|mailbox)\}/g, (match, key) => values[key] || match)
}

/** Base64 to bytes. */
export function base64Bytes(data) {
  return Uint8Array.from(atob(data || ''), (char) => char.charCodeAt(0))
}

const PREVIEW_TYPES = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
}

/**
 * `{ kind: 'image' | 'pdf', type }` when the app can show the attachment itself, else null. A
 * generic content type falls back to the file extension.
 */
export function previewOf(contentType, filename) {
  let type = String(contentType || '').toLowerCase().split(';')[0].trim()
  if (!type || type === 'application/octet-stream') type = PREVIEW_TYPES[String(filename || '').toLowerCase().split('.').pop()] || type
  if (!Object.values(PREVIEW_TYPES).includes(type)) return null
  return { kind: type === 'application/pdf' ? 'pdf' : 'image', type }
}

// Where people forward from, with how to set the forward up there. `hosts` match the outside
// address's domain (and its subdomains); the last entry is everyone else.
const PROVIDERS = [
  {
    key: 'gmail',
    name: 'Gmail',
    hosts: ['gmail.com', 'googlemail.com'],
    icon: 'google-logo',
    steps: [
      'In Gmail open Settings, See all settings, Forwarding and POP/IMAP.',
      'Click Add a forwarding address and enter {to}.',
      'Gmail mails a confirmation code to that address. It shows up here on this source; enter the code in Gmail or open its link.',
      'Pick Forward a copy of incoming mail to {to}, keep Gmail\'s copy, and Save Changes.',
    ],
  },
  {
    key: 'outlook',
    name: 'Outlook',
    hosts: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'onmicrosoft.com'],
    icon: 'microsoft-outlook-logo',
    steps: [
      'In Outlook on the web open Settings, Mail, Forwarding.',
      'Turn on Enable forwarding, enter {to}, tick Keep a copy of forwarded messages, and Save.',
      'A work or school account may refuse to forward outside the organization; its administrator can allow it.',
    ],
  },
  {
    key: 'icloud',
    name: 'iCloud',
    hosts: ['icloud.com', 'me.com', 'mac.com'],
    icon: 'apple-logo',
    steps: ['On icloud.com open Mail, Settings, Mail Forwarding.', 'Turn on Forward my email to, enter {to}, and save.'],
  },
  {
    key: 'yahoo',
    name: 'Yahoo',
    hosts: ['yahoo.com', 'ymail.com', 'rocketmail.com'],
    icon: 'envelope-simple',
    steps: [
      'In Yahoo Mail open Settings, More Settings, Mailboxes, and the account.',
      'Enter {to} as the forwarding address and click Verify.',
      'Yahoo mails a link to that address; it arrives in this mailbox. Open it to finish.',
    ],
  },
  {
    key: 'other',
    name: 'Other',
    hosts: [],
    icon: 'envelope-simple',
    steps: [
      'Find Forwarding (or Rules) in the provider\'s mail settings.',
      'Forward every message to {to} and keep a copy there if you want one.',
    ],
  },
]

/** The provider an outside address belongs to: `{ key, name, icon, steps }`. */
export function sourceProvider(address) {
  const host = String(address || '').toLowerCase().split('@')[1] || ''
  return PROVIDERS.find((item) => item.hosts.some((name) => host === name || host.endsWith(`.${name}`))) || PROVIDERS[PROVIDERS.length - 1]
}

/** How a source is named in lists: its name, else its address, else the forwarder it came through. */
export function sourceLabel(source) {
  if (!source) return ''
  if (source.name && source.name !== source.address && source.name !== source.forwarder) return source.name
  return source.address || `via ${source.forwarder}`
}

/** `support@acme.com` + `gmail` -> `support+gmail@acme.com`: where a source forwards to. */
export function taggedAddress(address, tag) {
  const [local, host] = String(address || '').split('@')
  return tag && host ? `${local.split('+')[0]}+${tag}@${host}` : address || ''
}

/** A tag to suggest for a new source: the provider's key, else the address's domain name. */
export function suggestTag(address) {
  const provider = sourceProvider(address)
  if (provider.key !== 'other') return provider.key
  const host = String(address || '').toLowerCase().split('@')[1] || ''
  return host.split('.').slice(-2, -1)[0]?.replace(/[^a-z0-9._-]/g, '') || ''
}
